import { useRef, useState, type ClipboardEvent, type FormEvent } from "react";
import { Trash2Icon } from "lucide-react";
import { Alert, AlertDescription } from "@/components/ui/alert";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { delFetcher, postFetcher, putFetcher } from "@/lib/swr";
import { toastError } from "@/lib/toast";

// 我的执行 prompt 模板编辑器(WED-206):变量白名单与当前 PRD 的变量值都由
// GET /prds/:id/exec-prompt 给出,这里只做插入、预览和粘贴反向识别;
// 保存时服务端再校验一次变量。

export type ExecVariable = { key: string; label: string };
export type ExecTemplate = {
  id: string; name: string; body: string; originPublicationId?: string | null;
};

const VAR_PATTERN = /\{\{(\w+)\}\}/g;
const TEMPLATES_PATH = "/me/exec-templates";

export const renderTemplate = (body: string, vars: Record<string, string>) =>
  body.replace(VAR_PATTERN, (m, k: string) =>
    Object.prototype.hasOwnProperty.call(vars, k) ? vars[k] : m,
  );

export const unknownVariables = (body: string, variables: ExecVariable[]) => [
  ...new Set(
    [...body.matchAll(VAR_PATTERN)]
      .map((m) => m[1])
      .filter((k) => !variables.some((v) => v.key === k)),
  ),
];

/**
 * 正文里出现的当前 PRD 变量值(长的优先,避免短值吃掉长值的一部分)。
 * 太短的值(< 3 字符)容易误伤,不识别。
 */
export const detectVariables = (
  body: string,
  vars: Record<string, string>,
) =>
  Object.entries(vars)
    .filter(([, v]) => v.length >= 3 && body.includes(v))
    .sort(([, a], [, b]) => b.length - a.length)
    .map(([key, value]) => ({ key, value }));

export const replaceWithVariables = (
  body: string,
  found: { key: string; value: string }[],
) =>
  found.reduce((s, { key, value }) => s.split(value).join(`{{${key}}}`), body);

export const ExecTemplateEditor = ({
  open,
  onOpenChange,
  template,
  variables,
  vars,
  onSaved,
  onDeleted,
  previewLabel = "预览（当前 PRD）",
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** 编辑已有模板;缺省为新建 */
  template?: ExecTemplate;
  variables: ExecVariable[];
  vars: Record<string, string>;
  onSaved: (t: ExecTemplate) => void;
  onDeleted: (id: string) => void;
  previewLabel?: string;
}) => {
  const { toast } = useToast();
  const [name, setName] = useState(template?.name ?? "");
  const [body, setBody] = useState(template?.body ?? "");
  const [found, setFound] = useState<{ key: string; value: string }[]>([]);
  const [saving, setSaving] = useState(false);
  const bodyRef = useRef<HTMLTextAreaElement>(null);

  const unknown = unknownVariables(body, variables);
  const labelOf = (key: string) =>
    variables.find((v) => v.key === key)?.label ?? key;

  const insert = (key: string) => {
    const el = bodyRef.current;
    const token = `{{${key}}}`;
    const [start, end] = el
      ? [el.selectionStart, el.selectionEnd]
      : [body.length, body.length];
    setBody(body.slice(0, start) + token + body.slice(end));
    requestAnimationFrame(() => {
      el?.focus();
      el?.setSelectionRange(start + token.length, start + token.length);
    });
  };
  // 贴进来的多半是某次用过的 prompt,写死了短 ID / 标题 / 链接:提示一键换成变量
  const onPaste = (e: ClipboardEvent<HTMLTextAreaElement>) => {
    const pasted = e.clipboardData.getData("text");
    const hits = detectVariables(pasted, vars);
    if (hits.length) setFound(hits);
  };
  const applyFound = () => {
    setBody(replaceWithVariables(body, found));
    setFound([]);
  };

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (!name.trim() || !body.trim() || unknown.length) return;
    setSaving(true);
    try {
      const arg = { name: name.trim(), body };
      const saved = template
        ? await putFetcher<ExecTemplate>(`${TEMPLATES_PATH}/${template.id}`, {
            arg,
          })
        : await postFetcher<ExecTemplate>(TEMPLATES_PATH, { arg });
      onSaved(saved);
      onOpenChange(false);
    } catch (err) {
      toastError(toast, "保存模板失败", err);
    } finally {
      setSaving(false);
    }
  };
  const remove = async () => {
    if (!template) return;
    try {
      await delFetcher(TEMPLATES_PATH, { arg: template.id });
      onDeleted(template.id);
      onOpenChange(false);
    } catch (err) {
      toastError(toast, "删除模板失败", err);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-3xl">
        <form onSubmit={submit} className="grid min-w-0 gap-4">
          <DialogHeader>
            <DialogTitle>{template ? "编辑我的模板" : "新建我的模板"}</DialogTitle>
            <DialogDescription>
              只有你自己能看到和使用。用变量代替写死的内容，执行时按当前 PRD
              替换。
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-2">
            <Label htmlFor="exec-template-name">名称</Label>
            <Input
              id="exec-template-name"
              value={name}
              maxLength={64}
              onChange={(e) => setName(e.target.value)}
              placeholder="比如：写单测"
            />
          </div>
          <div className="grid gap-2">
            <Label htmlFor="exec-template-body">模板</Label>
            <div className="flex flex-wrap gap-1.5" aria-label="插入变量">
              {variables.map((v) => (
                <Button
                  key={v.key}
                  type="button"
                  variant="outline"
                  size="xs"
                  title={`插入 {{${v.key}}}`}
                  onClick={() => insert(v.key)}
                >
                  {v.label}
                </Button>
              ))}
            </div>
            <Textarea
              id="exec-template-body"
              ref={bodyRef}
              value={body}
              onChange={(e) => setBody(e.target.value)}
              onPaste={onPaste}
              maxLength={20000}
              placeholder="粘贴你常用的 prompt，或点上面的变量插入"
              className="max-h-[35vh] font-mono text-xs"
            />
            {found.length > 0 && (
              <Alert>
                <AlertDescription className="flex flex-wrap items-center gap-2">
                  <span>
                    检测到当前 PRD 的
                    {found.map((f) => `${labelOf(f.key)}「${f.value}」`).join("、")}
                    ，替换成变量？
                  </span>
                  <Button type="button" size="xs" onClick={applyFound}>
                    替换
                  </Button>
                  <Button
                    type="button"
                    size="xs"
                    variant="ghost"
                    onClick={() => setFound([])}
                  >
                    忽略
                  </Button>
                </AlertDescription>
              </Alert>
            )}
            {unknown.length > 0 && (
              <p className="text-sm text-destructive">
                未知变量 {unknown.map((k) => `{{${k}}}`).join("、")}
                ，只能用上面列出的变量
              </p>
            )}
          </div>
          <div className="grid gap-2">
            <Label htmlFor="exec-template-preview">{previewLabel}</Label>
            <Textarea
              id="exec-template-preview"
              readOnly
              value={renderTemplate(body, vars)}
              className="max-h-[25vh] bg-muted font-mono text-xs"
            />
          </div>
          <DialogFooter className="sm:justify-between">
            {template ? (
              <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button type="button" variant="destructive">
                    <Trash2Icon />
                    删除
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>删除模板「{template.name}」？</AlertDialogTitle>
                    <AlertDialogDescription>删除后无法恢复。</AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>取消</AlertDialogCancel>
                    <AlertDialogAction onClick={remove}>删除</AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog>
            ) : (
              <span />
            )}
            <Button
              type="submit"
              disabled={saving || !name.trim() || !body.trim() || unknown.length > 0}
            >
              保存
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
};
